/**
 * Open/closed state for the phone "More" sheet (components/Nav/MoreSheet.tsx,
 * #2142).
 *
 * A module store rather than React state because three unrelated places drive
 * it: the More tab opens it, the sheet closes it, and the /more deep link opens
 * it after redirecting to a page that has the tab bar.
 *
 * ## Back closes the sheet
 *
 * Opening pushes a same-URL history entry (`{ rasMoreSheet: true }`), so the
 * browser back button and the Android back button close the sheet instead of
 * leaving the page underneath it. Vike's router supports that: an entry pushed
 * through `history.pushState` is stamped `triggeredBy: 'user'`, and popping
 * back from it to the same URL does not re-render the page.
 *
 * Every other way of closing (backdrop, swipe down, Escape, picking an item)
 * goes back through that same entry, so it never lingers as a dead "back" stop.
 * Picking an item passes the navigation as `then`, which runs only after the
 * pop has landed; navigating first would leave the sheet's entry in the stack
 * under the new page.
 *
 * ## Only while a tab bar is mounted
 *
 * The sheet belongs to the phone tab bar (components/Nav/MobileTabBar.tsx),
 * which registers itself here while mounted. Opening is a no-op without one
 * (e.g. /more after the guard bounced to /signin), and the sheet is dropped
 * when the last bar unmounts (a forced sign-out lands on a HIDE_ON page), so
 * stale open state can never resurface when the user comes back.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

let open = false;
/** The pathname the sheet was opened on; a route change away from it closes the sheet. */
let openedOn: string | null = null;
/** Whether the current history entry is the one the sheet pushed. */
let ownsEntry = false;
let afterClose: (() => void) | null = null;
let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
let popListening = false;
let tabBars = 0;
let unmountTimer: ReturnType<typeof setTimeout> | undefined;

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeMoreSheet(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isMoreSheetOpen() {
  return open;
}

export function moreSheetOpenedOn() {
  return openedOn;
}

function finishClose() {
  clearTimeout(fallbackTimer);
  ownsEntry = false;
  openedOn = null;

  if (open) {
    open = false;
    emit();
  }

  const then = afterClose;
  afterClose = null;
  // Deferred so Vike's own popstate handling (which sees a same-page pop and
  // skips rendering) settles before a follow-up navigation starts.
  if (then) {
    setTimeout(then, 0);
  } else {
    // Nothing navigates after a plain close, so no page transition should be in
    // flight. Clear any the same-page pop left behind (the top loading bar).
    setTimeout(clearStaleTransition, 0);
  }
}

function clearStaleTransition() {
  document.body?.classList.remove('page-is-transitioning');
  document.getElementById('transitionstyle')?.remove();
}

function onPopState() {
  if (open && !window.history.state?.rasMoreSheet) {
    finishClose();
  }
}

/**
 * Called by each mounted tab bar; returns its unregister. The last unregister
 * drops the sheet a tick later, so a bar that is swapped for another in the
 * same commit (a Suspense fallback resolving) doesn't close it.
 */
export function registerTabBar() {
  tabBars += 1;
  clearTimeout(unmountTimer);

  return () => {
    tabBars -= 1;
    if (tabBars === 0) {
      clearTimeout(unmountTimer);
      unmountTimer = setTimeout(() => {
        if (tabBars === 0) {
          dismissMoreSheet();
        }
      }, 0);
    }
  };
}

export function openMoreSheet() {
  if (open || tabBars === 0) {
    return;
  }

  open = true;
  openedOn = window.location.pathname;

  if (!popListening) {
    popListening = true;
    window.addEventListener('popstate', onPopState);
  }

  try {
    window.history.pushState({ rasMoreSheet: true }, '');
    ownsEntry = true;
  } catch {
    ownsEntry = false;
  }

  emit();
}

/**
 * Close the sheet, then run `then` (e.g. a navigation) once its history entry
 * is gone.
 */
export function closeMoreSheet(then?: () => void) {
  if (!open) {
    then?.();
    return;
  }

  afterClose = then ?? null;

  if (ownsEntry && window.history.state?.rasMoreSheet) {
    window.history.back();
    // popstate normally lands within a frame; never leave the sheet stuck open
    // (or the navigation unrun) if it does not.
    clearTimeout(fallbackTimer);
    fallbackTimer = setTimeout(finishClose, 400);
    return;
  }

  finishClose();
}

/**
 * Drop the sheet without touching history: the page changed underneath it
 * (e.g. a forced sign-out redirect), so its entry is no longer the current one.
 */
export function dismissMoreSheet() {
  if (!open) {
    return;
  }

  afterClose = null;
  finishClose();
}
